import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/client";

type Question = {
  id: string;
  text: string;
  options: Array<{ id: string; text: string }>;
};

type AssessmentData = {
  assessmentId: string;
  title: string;
  passingScore: number;
  questions: Question[];
};

export function Assessment() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<AssessmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Array<{ questionId: string; optionIndex: number }>>([]);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<{ score: number; passed: boolean; total: number; correct: number } | null>(null);

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/courses/${slug}/assessment`)
      .then((res) => {
        if (res.data.success) setData(res.data.data);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const setAnswer = (questionId: string, optionIndex: number) => {
    setAnswers((prev) => {
      const rest = prev.filter((a) => a.questionId !== questionId);
      return [...rest, { questionId, optionIndex }];
    });
  };

  const handleSubmit = () => {
    if (!slug || !data) return;
    const payload = data.questions.map((q) => ({
      questionId: q.id,
      optionIndex: answers.find((a) => a.questionId === q.id)?.optionIndex ?? -1,
    }));
    api.post(`/courses/${slug}/assessment`, { answers: payload }).then((res) => {
      setResult(res.data.data);
      setSubmitted(true);
    });
  };

  const answeredCount = answers.length;

  if (loading) {
    return (
      <div className="min-h-full bg-stone/40">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-8 w-48 bg-gray-200 rounded" />
            <div className="h-6 w-3/4 bg-gray-200 rounded" />
            <div className="space-y-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-28 bg-gray-100 rounded-2xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-full bg-stone/40 flex items-center justify-center px-4 py-16">
        <div className="rounded-2xl border border-red-200 bg-white p-10 text-center shadow-soft max-w-md">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center text-red-500">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-red-600 font-medium mt-4">Assessment not found.</p>
          <Link to={`/dashboard/courses/${slug}`} className="mt-6 inline-block font-semibold text-gold-500 hover:text-gold-400 transition-colors">
            Back to course
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-stone/40">
      <div className="bg-ink-950 text-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to={`/dashboard/courses/${slug}`}
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to course
          </Link>
          <p className="text-gold-300/90 font-semibold text-xs tracking-[0.2em] uppercase mt-6">
            Final assessment
          </p>
          <h1 className="font-serif text-2xl md:text-3xl font-semibold tracking-tight mt-2">
            {data.title}
          </h1>
          <p className="mt-2 text-slate-400 text-sm">
            Passing score: {data.passingScore}% · {data.questions.length} question{data.questions.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold-400/30 to-transparent" aria-hidden />
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {!submitted ? (
          <>
            <div className="mb-8 rounded-xl bg-white border border-gray-200 px-4 py-3 shadow-soft flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">
                Progress: {answeredCount} of {data.questions.length} answered
              </span>
              <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gold-400 rounded-full transition-all duration-300"
                  style={{ width: `${(answeredCount / data.questions.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="space-y-8">
              {data.questions.map((q, qIdx) => (
                <div key={q.id} className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-soft">
                  <p className="text-xs font-semibold text-gold-500 uppercase tracking-wider mb-2">
                    Question {qIdx + 1} of {data.questions.length}
                  </p>
                  <p className="font-medium text-ink-950 text-lg">
                    {q.text}
                  </p>
                  <div className="mt-5 space-y-2">
                    {q.options.map((opt, optIdx) => (
                      <label
                        key={opt.id}
                        className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3.5 cursor-pointer hover:border-gold-400/40 hover:bg-stone/30 transition-colors has-[:checked]:border-gold-400 has-[:checked]:bg-gold-50/50"
                      >
                        <input
                          type="radio"
                          name={q.id}
                          checked={answers.find((a) => a.questionId === q.id)?.optionIndex === optIdx}
                          onChange={() => setAnswer(q.id, optIdx)}
                          className="text-gold-500 focus:ring-gold-400"
                        />
                        <span className="text-ink-950">{opt.text}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={handleSubmit}
                className="rounded-lg bg-gold-400 px-6 py-3.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors shadow-md"
              >
                Submit assessment
              </button>
              <p className="text-sm text-gray-500">
                Make sure you’ve answered all questions before submitting.
              </p>
            </div>
          </>
        ) : result ? (
          <div
            className={`rounded-2xl border-2 overflow-hidden shadow-soft-lg ${
              result.passed
                ? "border-green-200 bg-white"
                : "border-amber-200 bg-white"
            }`}
          >
            <div className={`px-8 py-6 ${result.passed ? "bg-green-50" : "bg-amber-50"}`}>
              <div className="flex items-center gap-4">
                <div
                  className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${
                    result.passed ? "bg-green-100 text-green-600" : "bg-amber-100 text-amber-600"
                  }`}
                >
                  {result.passed ? (
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                </div>
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-ink-950 tracking-tight">
                    {result.passed ? "You passed!" : "Not quite"}
                  </h2>
                  <p className={`text-2xl font-bold mt-0.5 ${result.passed ? "text-green-600" : "text-amber-600"}`}>
                    {result.score}%
                  </p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <p className="text-gray-600">
                {result.correct} of {result.total} correct. Passing score: {data.passingScore}%.
              </p>
              {result.passed && (
                <Link
                  to={`/dashboard/courses/${slug}/certificate`}
                  className="mt-8 inline-flex items-center gap-2 rounded-lg bg-gold-400 px-6 py-3.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors shadow-md"
                >
                  Get your certificate
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
              )}
              {!result.passed && (
                <Link
                  to={`/dashboard/courses/${slug}`}
                  className="mt-8 inline-block font-semibold text-gold-500 hover:text-gold-400 transition-colors"
                >
                  Back to course to review
                </Link>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
